import { ElementSelectionService } from '../../..';
import { findAncestor } from '../../../helpers/dom/node';
import {
  createPointAtEndOfNodeContents,
  createPointAtStartOfNode,
  createPointAtStartOfNodeContents,
} from '../../../helpers/dom/point';
import {
  addColumnLeftToCell,
  addColumnRightToCell,
  addRowAboveCell,
  addRowUnderCell,
  deleteCells,
  getCellCoordinateInPlan,
  getCellCountInPlan,
  getFirstCellInPlan,
  getFirstColumnCells,
  getHorizontalAlignedCells,
  getLastCellInPlan,
  getTablePlan,
  getVerticalAlignedCells,
  isSameTablePlan,
  isTableCellElement,
  isTableElement,
  TablePlan,
} from '../../../helpers/dom/table';
import { createPointsWithSelection } from '../../../helpers/selection';
import {
  appendButtonPanel,
  appendButtonPanelToDelete,
  decorateCellHeaderAsSelected,
  decorateCellsAsSelected,
  TABLE_FULLY_SELECTED,
  undecorateButtonPanels,
  undecorateCellsAsSelected,
  undecorateTableForEdition,
  updateTableHeaders,
} from './decorateTable';

export class TableEditionController {
  constructor(element: Element, selectionService: ElementSelectionService) {
    this.element = element;
    this.selectionService = selectionService;
  }

  private readonly element: Element;
  private readonly selectionService: ElementSelectionService;
  private previouslyDecoratedTable: HTMLTableElement | null = null;
  private previouslySelectedRowColumn: TablePlan | null = null;

  onColumnHeaderDecoratorClicked(handle: Element) {
    this.onHeaderDecoratorClicked(handle, 'column');
  }

  onRowHeaderDecoratorClicked(handle: Element) {
    this.onHeaderDecoratorClicked(handle, 'row');
  }

  onCornerButtonDecoratorClicked(cornerButton: Element) {
    const cell: HTMLTableCellElement | null = findAncestor(cornerButton, isTableCellElement, this.element);
    if (!cell) {
      return;
    }
    const table: HTMLTableElement | null = findAncestor(cell, isTableElement, this.element);
    if (!table) {
      return;
    }

    // Select the whole table.
    const tablePlan = getTablePlan(table);
    const firstCell = getFirstCellInPlan(tablePlan);
    const lastCell = getLastCellInPlan(tablePlan);
    if (!firstCell || !lastCell) {
      return;
    }
    const points = {
      anchorPoint: createPointAtStartOfNodeContents(firstCell),
      focusPoint: createPointAtEndOfNodeContents(lastCell),
    };
    this.selectionService.selectWithPoints(points);

    // Show the whole table as being selected.
    const firstColumnCells = getFirstColumnCells(table);
    const firstRowCells = tablePlan[0];
    firstColumnCells.forEach(c => decorateCellHeaderAsSelected(c, 'row'));
    firstRowCells.forEach(c => decorateCellHeaderAsSelected(c, 'column'));
    table.classList.add(TABLE_FULLY_SELECTED);

    // Display the button panel.
    undecorateButtonPanels(table);
    appendButtonPanelToDelete(cell, table, () => {
      undecorateTableForEdition(table);
      table.outerHTML = '';
      table.remove();
    });
    this.previouslySelectedRowColumn = tablePlan;
  }

  private onHeaderDecoratorClicked(handle: Element, direction: 'row' | 'column') {
    const cell: HTMLTableCellElement | null = findAncestor(handle, isTableCellElement, this.element);
    if (!cell) {
      return;
    }
    const table: HTMLTableElement | null = findAncestor(cell, isTableElement, this.element);
    if (!table) {
      return;
    }

    // Set selection matching the row / column.
    const selectionPlan =
      direction === 'row' ? getHorizontalAlignedCells(table, cell) : getVerticalAlignedCells(table, cell);
    const firstCell = getFirstCellInPlan(selectionPlan);
    const lastCell = getLastCellInPlan(selectionPlan);
    if (!firstCell || !lastCell) {
      return;
    }
    const points = {
      anchorPoint: createPointAtStartOfNodeContents(firstCell),
      focusPoint: createPointAtStartOfNodeContents(lastCell),
    };
    this.selectionService.selectWithPoints(points);
    // Special style for the header.
    decorateCellHeaderAsSelected(firstCell, direction, true);
    // Add the buttonPanel.
    undecorateButtonPanels(table);
    appendButtonPanel(
      cell,
      table,
      direction,
      () => {
        if (direction === 'row') {
          addRowAboveCell(table, cell);
        } else {
          addColumnLeftToCell(table, cell);
        }
        updateTableHeaders(table);
        this.selectionService.moveToOffsetInContainer(cell, 0);
      },
      () => {
        if (direction === 'row') {
          addRowUnderCell(table, cell);
        } else {
          addColumnRightToCell(table, cell);
        }
        updateTableHeaders(table);
        this.selectionService.moveToOffsetInContainer(cell, 0);
      },
      () => deleteCells(table, selectionPlan),
    );
    this.previouslySelectedRowColumn = selectionPlan;
  }

  onSelectionPlanChange(selectionPlan: TablePlan | null) {
    // Undecorate previously selected cells.
    if (this.previouslyDecoratedTable !== null) {
      this.previouslyDecoratedTable.classList.remove(TABLE_FULLY_SELECTED);
      undecorateCellsAsSelected(this.previouslyDecoratedTable);
      if (!isSameTablePlan(this.previouslySelectedRowColumn, selectionPlan)) {
        undecorateButtonPanels(this.previouslyDecoratedTable);
      }
    }
    // If selection is empty, do nothing.
    if (selectionPlan === null || getCellCountInPlan(selectionPlan) === 0) {
      this.previouslyDecoratedTable = null;
      return;
    }
    const table = findAncestor(selectionPlan[0][0], isTableElement, this.element);
    if (table === null) {
      this.previouslyDecoratedTable = null;
      return;
    }
    decorateCellsAsSelected(selectionPlan, table);
    this.previouslyDecoratedTable = table;
  }

  onShiftArrowKeyPressed(key: string, event: KeyboardEvent) {
    // Check if there is a selection and if it starts and ends in the same table.
    const { selection } = this.selectionService;
    if (!selection) {
      return;
    }
    const { anchorNode, focusNode } = selection;
    const anchorCell = findAncestor(anchorNode, isTableCellElement, this.element);
    const focusCell = findAncestor(focusNode, isTableCellElement, this.element);
    const anchorTable = findAncestor(anchorNode, isTableElement, this.element);
    const focusTable = findAncestor(anchorNode, isTableElement, this.element);
    if (!anchorCell || !focusCell || !anchorTable || anchorTable !== focusTable) {
      return;
    }
    // Compute new coordinates of focusCell.
    const tablePlan = getTablePlan(anchorTable);
    const coordinates = getCellCoordinateInPlan(focusCell, tablePlan);
    if (!coordinates) {
      return;
    }
    let rowCoordinateModifier = 0;
    if (key === arrowKeyCode.UP) {
      rowCoordinateModifier = -1;
    }
    if (key === arrowKeyCode.DOWN) {
      rowCoordinateModifier = focusCell.rowSpan;
    }
    let columnCoordinateModifier = 0;
    if (key === arrowKeyCode.LEFT) {
      columnCoordinateModifier = -1;
    }
    if (key === arrowKeyCode.RIGHT) {
      columnCoordinateModifier = focusCell.colSpan;
    }
    const newCoordinate = {
      row: coordinates.row + rowCoordinateModifier,
      column: coordinates.column + columnCoordinateModifier,
    };
    const newFocusCell = tablePlan[newCoordinate.row]
      ? tablePlan[newCoordinate.row][newCoordinate.column] || focusCell
      : focusCell;
    // Create new selection from same anchorCell to new focusCell.
    const points = {
      anchorPoint: createPointsWithSelection(selection).anchorPoint,
      focusPoint: createPointAtStartOfNode(newFocusCell.firstChild || newFocusCell),
    };
    event.preventDefault();
    this.selectionService.selectWithPoints(points);
  }

  onUpDownArrowKeyPressed(key: string, event: KeyboardEvent) {
    // Check if there is a selection and if it starts and ends in the same table.
    const { selection } = this.selectionService;
    if (!selection || !selection.isCollapsed) {
      return;
    }
    const { anchorNode } = selection;
    const anchorCell = findAncestor(anchorNode, isTableCellElement, this.element);
    const anchorTable = findAncestor(anchorNode, isTableElement, this.element);
    if (!anchorCell || !anchorTable) {
      return;
    }
    // Compute new coordinates of focusCell.
    const tablePlan = getTablePlan(anchorTable);
    const coordinates = getCellCoordinateInPlan(anchorCell, tablePlan);
    if (!coordinates) {
      return;
    }
    let rowCoordinateModifier = 0;
    if (key === arrowKeyCode.UP) {
      rowCoordinateModifier = -1;
    }
    if (key === arrowKeyCode.DOWN) {
      rowCoordinateModifier = anchorCell.rowSpan;
    }
    const newCoordinate = {
      row: coordinates.row + rowCoordinateModifier,
      column: coordinates.column,
    };
    const newFocusCell = tablePlan[newCoordinate.row]
      ? tablePlan[newCoordinate.row][newCoordinate.column] || anchorCell
      : anchorCell;
    this.selectionService.moveToOffsetInContainer(newFocusCell, 0);
    event.preventDefault();
  }
}

export const arrowKeyCode = {
  LEFT: 'ArrowLeft',
  UP: 'ArrowUp',
  RIGHT: 'ArrowRight',
  DOWN: 'ArrowDown',
};
